/**
 * Phase 12 — Atomare Dexie-Transaktion: KV-Blob + domain_aggregate (+ optional Outbox).
 * Verhindert Blob/Index-Spaltung und Outbox ohne zugehörigen Blob-Write.
 */

import { normalizeBusinessPayloadShape } from '../../utils/sharedCore';
import { compressData, shouldCompressKvKey } from '../../utils/localStorageCompression';
import { getElkheirDb } from './elkheirDb';
import { getStorageEngineRef } from './storageEngineRef';
import {
  isDomainAggregateIndexEnabled,
  syncAggregateIndexFromPayload
} from './domainAggregateStore';
import {
  getEntityTablesForFields,
  isEntityStoreEnabled,
  syncEntityStoresFromPayload
} from './entityAggregateStore';
import { runEntityStoreWrite } from './entityStoreActivityState';
import { buildOutboxRow } from './outboxAccess';
import { businessPayloadMetaKey, writeBusinessPayloadMeta } from './businessPayloadMeta';

/** @typedef {{ entityType: string, payload: unknown, id?: string }} OutboxEntryInput */

export function isDexieAtomicBusinessWriteEnabled() {
  const v = process.env.REACT_APP_DEXIE_ATOMIC_BUSINESS_WRITE;
  if (v === 'false' || v === '0') return false;
  try {
    return Boolean(getStorageEngineRef().usesIndexedDb?.());
  } catch {
    return false;
  }
}

/**
 * Serialisiert Business-Payload wie writeBusinessMergedPayload (inkl. Kompression).
 * @param {string} key
 * @param {object} payload
 */
export function serializeBusinessPayloadForKvStorage(key, payload) {
  const toWrite = normalizeBusinessPayloadShape(payload);
  if (shouldCompressKvKey(key)) {
    const compressionResult = compressData(key, toWrite);
    return JSON.stringify({
      data: compressionResult.data,
      compressed: compressionResult.compressed,
      originalSize: compressionResult.originalSize,
      compressedSize: compressionResult.compressedSize,
      compressionRatio: compressionResult.compressionRatio,
      timestamp: Date.now()
    });
  }
  return JSON.stringify(toWrite);
}

function syncKvBackendCache(kvKey, kvRawValue) {
  const engine = getStorageEngineRef();
  const backend = engine._backend;
  if (backend?.applyCachedKvWrite) {
    backend.applyCachedKvWrite(kvKey, kvRawValue);
  } else if (backend?.setItem) {
    backend.setItem(kvKey, kvRawValue);
  }
}

/**
 * @param {{
 *   kvKey: string,
 *   kvRawValue: string,
 *   businessId: string,
 *   payload: object,
 *   changedFields?: string[],
 *   outboxEntries?: OutboxEntryInput[] | ((ctx: { payload: object }) => OutboxEntryInput[])
 * }} params
 */
export async function runAtomicBusinessPersist({
  kvKey,
  kvRawValue,
  businessId,
  payload,
  changedFields = [],
  outboxEntries = []
}) {
  const db = getElkheirDb();
  if (!db.isOpen()) {
    await db.open();
  }

  const resolvedOutbox =
    typeof outboxEntries === 'function'
      ? outboxEntries({ payload })
      : outboxEntries || [];
  const outboxRows = resolvedOutbox.map((entry) => buildOutboxRow(entry));

  const tables = [db.kv_items];
  const syncAggregate =
    isDomainAggregateIndexEnabled() &&
    db.domain_aggregate &&
    changedFields.length > 0;
  const syncEntityStores =
    isEntityStoreEnabled() && changedFields.length > 0;
  if (syncAggregate) {
    tables.push(db.domain_aggregate);
  }
  if (syncEntityStores) {
    for (const table of getEntityTablesForFields(db, changedFields)) {
      if (!tables.includes(table)) tables.push(table);
    }
  }
  if (outboxRows.length > 0 && db.outbox) {
    tables.push(db.outbox);
  }

  const entityStoreCount = syncEntityStores
    ? getEntityTablesForFields(db, changedFields).length
    : 0;

  const runPersist = async () => {
    await db.transaction('rw', tables, async () => {
      await db.kv_items.put({ key: kvKey, value: kvRawValue, updatedAt: Date.now() });

      if (syncAggregate) {
        await syncAggregateIndexFromPayload(businessId, payload, changedFields, db);
      }

      if (syncEntityStores) {
        await syncEntityStoresFromPayload(db, businessId, payload, changedFields);
      }

      if (outboxRows.length > 0 && db.outbox) {
        await db.outbox.bulkPut(outboxRows);
      }
    });
  };

  if (entityStoreCount > 0) {
    await runEntityStoreWrite(
      'atomic_business_persist',
      { storeCount: entityStoreCount, fields: changedFields },
      runPersist
    );
  } else {
    await runPersist();
  }

  syncKvBackendCache(kvKey, kvRawValue);
  return { outboxRows };
}

/**
 * Phase 23 — entity_* + meta (payloadVersion) + Outbox, ohne kv_items business_*_data.
 * @param {{
 *   businessId: string,
 *   payload: object,
 *   changedFields?: string[],
 *   outboxEntries?: OutboxEntryInput[] | ((ctx: { payload: object }) => OutboxEntryInput[]),
 *   payloadMeta?: { payloadVersion: number, payloadUpdatedAt: string }
 * }} params
 */
export async function runAtomicEntityOnlyPersist({
  businessId,
  payload,
  changedFields = [],
  outboxEntries = [],
  payloadMeta
}) {
  const db = getElkheirDb();
  if (!db.isOpen()) {
    await db.open();
  }

  const resolvedOutbox =
    typeof outboxEntries === 'function'
      ? outboxEntries({ payload })
      : outboxEntries || [];
  const outboxRows = resolvedOutbox.map((entry) => buildOutboxRow(entry));

  const tables = [db.meta];
  const syncAggregate =
    isDomainAggregateIndexEnabled() &&
    db.domain_aggregate &&
    changedFields.length > 0;
  const syncEntityStores =
    isEntityStoreEnabled() && changedFields.length > 0;
  if (syncAggregate) {
    tables.push(db.domain_aggregate);
  }
  if (syncEntityStores) {
    for (const table of getEntityTablesForFields(db, changedFields)) {
      if (!tables.includes(table)) tables.push(table);
    }
  }
  if (outboxRows.length > 0 && db.outbox) {
    tables.push(db.outbox);
  }

  const metaKey = businessPayloadMetaKey(businessId);
  const entityStoreCount = syncEntityStores
    ? getEntityTablesForFields(db, changedFields).length
    : 0;

  const runPersist = async () => {
    await db.transaction('rw', tables, async () => {
      if (payloadMeta && metaKey) {
        await writeBusinessPayloadMeta(
          businessId,
          {
            payloadVersion: payloadMeta.payloadVersion,
            payloadUpdatedAt: payloadMeta.payloadUpdatedAt,
            noKvBlob: true
          },
          db
        );
      }

      if (syncAggregate) {
        await syncAggregateIndexFromPayload(businessId, payload, changedFields, db);
      }

      if (syncEntityStores) {
        await syncEntityStoresFromPayload(db, businessId, payload, changedFields);
      }

      if (outboxRows.length > 0 && db.outbox) {
        await db.outbox.bulkPut(outboxRows);
      }
    });
  };

  if (entityStoreCount > 0) {
    await runEntityStoreWrite(
      'atomic_entity_only_persist',
      { storeCount: entityStoreCount, fields: changedFields },
      runPersist
    );
  } else {
    await runPersist();
  }

  return { outboxRows };
}

/**
 * Phase C — single entity row + payload meta (no full-array sync / prune).
 * @param {{
 *   businessId: string,
 *   aggregate: string,
 *   item: object,
 *   payloadMeta?: { payloadVersion: number, payloadUpdatedAt: string }
 * }} params
 */
export async function runAtomicEntityItemPersist({
  businessId,
  aggregate,
  item,
  payloadMeta
}) {
  const { putEntityArrayItem } = await import('./entityAggregateStore');
  const db = getElkheirDb();
  if (!db.isOpen()) {
    await db.open();
  }

  const entityTables = getEntityTablesForFields(db, [aggregate]);
  if (entityTables.length === 0) {
    throw new Error(`runAtomicEntityItemPersist: no table for ${aggregate}`);
  }

  const tables = [db.meta, ...entityTables];
  const metaKey = businessPayloadMetaKey(businessId);

  await runEntityStoreWrite(
    'atomic_entity_item_persist',
    { storeCount: entityTables.length, fields: [aggregate] },
    async () => {
      await db.transaction('rw', tables, async () => {
        if (payloadMeta && metaKey) {
          await writeBusinessPayloadMeta(
            businessId,
            {
              payloadVersion: payloadMeta.payloadVersion,
              payloadUpdatedAt: payloadMeta.payloadUpdatedAt,
              noKvBlob: true
            },
            db
          );
        }
        await putEntityArrayItem(businessId, aggregate, item, db);
      });
    }
  );

  return { aggregate, entityId: item?.id || item?.productId || null };
}
